import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useParams } from "react-router-dom";
import api from "../../../api/axios";
import bgImage from "../../../assets/images/1768687732162_3D.png";
import { useAuth } from "../../../AuthContext";
import { getServicesFor } from "../../../data/services";

export default function BookingPage() {
  const { userId, serviceName } = useParams();
  const { user } = useAuth();

  const [selectedService, setSelectedService] = useState(null);
  const [date, setDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const availableServices = getServicesFor(serviceName); // [] لو lawyer/translator
  const needsServiceChoice = availableServices.length > 0;

  // =========================
  // توليد الأوقات
  // =========================
  const generateTimeSlots = (start = 8, end = 17, interval = 30) => {
    const slots = [];
    let current = start * 60;
    const endMinutes = end * 60;

    while (current < endMinutes) {
      const h = String(Math.floor(current / 60)).padStart(2, "0");
      const m = String(current % 60).padStart(2, "0");
      slots.push(`${h}:${m}`);
      current += interval;
    }

    return slots;
  };

  // =========================
  // جلب المواعيد المحجوزة
  // =========================
  useEffect(() => {
    if (!userId) return;

    const fetchUnavailable = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get("/appointments/unavailable", {
          params: {
            artisan_id: userId,
            date: date.toISOString().split("T")[0],
          },
        });

        setBookedSlots(res.data.data || []);
        setTimeSlots(generateTimeSlots());
      } catch (e) {
        console.error(e);
        setError("تعذر تحميل المواعيد");
      } finally {
        setLoading(false);
      }
    };

    fetchUnavailable();
  }, [userId, date]);

  // =========================
  // حجز موعد
  // =========================
  const bookSlot = async (time) => {
    setSuccessMsg("");

    if (!user) {
      setError("يجب تسجيل الدخول أولاً");
      return;
    }

    if (needsServiceChoice && !selectedService) {
      setError("يرجى اختيار نوع الخدمة أولاً");
      return;
    }

    try {
      await api.post("/appointments/book", {
        artisan_id: userId,
        date: date.toISOString().split("T")[0],
        time,
        service_key: selectedService?.key || null,
        lang: "ar",
      });

      setBookedSlots((prev) => [...prev, time]);
      setError("");
      setSuccessMsg(`تم حجز الموعد الساعة ${time} بنجاح`);
    } catch (e) {
      console.log(e);
      console.log(e.response);
      console.log(e.response?.data);

      if (e.response?.status === 409) {
        setError("هذا الموعد محجوز بالفعل");
      } else if (e.response?.status === 201) {
        setSuccessMsg(`تم حجز الموعد الساعة ${time} بنجاح`);
      } else {
        setError(e.response?.data?.message || "حدث خطأ أثناء الحجز");
      }
    }
  };

  const canPickTime = !needsServiceChoice || !!selectedService;

  return (
    <div dir="rtl" className="relative min-h-screen bg-slate-50">
      {/* خلفية مع طبقة تعتيم لتحسين التباين وقابلية القراءة */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <div className="absolute inset-0 bg-slate-900/40" />

      <div className="relative z-10 px-4 py-8 sm:px-6 lg:py-12">
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
          {/* عنوان الصفحة */}
          <div className="text-center">
            <h1 className="text-2xl font-extrabold text-white drop-shadow-sm sm:text-3xl">
              احجز موعدك
            </h1>
            <p className="mt-1 text-sm text-white/80">
              اختر الخدمة، ثم التاريخ والوقت المناسبين لك
            </p>
          </div>

          {/* شريط تقدّم بسيط يوضح للمستخدم أين هو في العملية */}
          <div className="flex items-center justify-center gap-2 text-xs font-medium">
            <StepBadge
              active
              done={!needsServiceChoice || !!selectedService}
              label="الخدمة"
              show={needsServiceChoice}
            />
            <StepDivider show={needsServiceChoice} />
            <StepBadge
              active={canPickTime}
              done={bookedSlots.length > 0 && false}
              label="التاريخ والوقت"
            />
          </div>

          {/* اختيار نوع الخدمة */}
          {needsServiceChoice && (
            <section className="rounded-3xl bg-white/95 p-6 shadow-xl ring-1 ring-black/5 backdrop-blur-md">
              <SectionTitle icon="📋" title="اختر نوع الخدمة" />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {availableServices.map((service) => {
                  const isSelected = selectedService?.key === service.key;
                  return (
                    <button
                      key={service.key}
                      onClick={() => setSelectedService(service)}
                      className={`group relative rounded-2xl border p-4 text-right transition-all duration-200 ${
                        isSelected
                          ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-400"
                          : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800">
                          {service.title}
                        </span>
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] transition-colors ${
                            isSelected
                              ? "border-emerald-500 bg-emerald-500 text-white"
                              : "border-slate-300 text-transparent"
                          }`}
                        >
                          ✓
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* الوثائق المطلوبة لنفس الخدمة المختارة */}
              <div className="mt-4 min-h-[140px] rounded-2xl border border-slate-200 bg-slate-50 p-4">
                {selectedService ? (
                  <>
                    <p className="mb-2 text-sm font-semibold text-slate-700">
                      الوثائق المطلوبة
                    </p>
                    <ul className="flex flex-col gap-1.5">
                      {selectedService.requirements.map((req, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2 text-sm text-slate-600"
                        >
                          <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="flex h-full items-center justify-center text-sm text-slate-400">
                    اختر خدمة لعرض الوثائق المطلوبة
                  </p>
                )}
              </div>
            </section>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {/* التقويم */}
            <section className="rounded-3xl bg-white/95 p-6 shadow-xl ring-1 ring-black/5 backdrop-blur-md">
              <SectionTitle icon="📅" title="اختر التاريخ" />

              <Calendar
                onChange={setDate}
                value={date}
                className="w-full rounded-2xl border-none [&_.react-calendar__tile--active]:!bg-emerald-500 [&_.react-calendar__tile--now]:!bg-emerald-50"
              />

              <div className="mt-4 rounded-2xl bg-slate-100 p-3 text-center font-medium text-slate-700">
                {date.toLocaleDateString("ar-DZ", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </section>

            {/* الأوقات */}
            <section className="rounded-3xl bg-white/95 p-6 shadow-xl ring-1 ring-black/5 backdrop-blur-md">
              <SectionTitle icon="🕒" title="الأوقات المتاحة" />

              {!canPickTime && (
                <Banner tone="amber">
                  اختر نوع الخدمة أولاً لإتاحة الأوقات
                </Banner>
              )}

              {error && <Banner tone="red">{error}</Banner>}
              {successMsg && <Banner tone="emerald">{successMsg}</Banner>}

              {loading ? (
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-11 animate-pulse rounded-xl bg-slate-200"
                    />
                  ))}
                </div>
              ) : (
                <>
                  <div
                    className={`grid grid-cols-3 gap-3 ${
                      !canPickTime ? "pointer-events-none opacity-40" : ""
                    }`}
                  >
                    {timeSlots.map((time) => {
                      const disabled = bookedSlots.includes(time);

                      return (
                        <button
                          key={time}
                          disabled={disabled || !canPickTime}
                          onClick={() => bookSlot(time)}
                          className={`rounded-xl py-3 text-sm font-semibold transition-all duration-200 ${
                            disabled
                              ? "cursor-not-allowed bg-slate-100 text-slate-400 line-through"
                              : "bg-emerald-500 text-white shadow-sm hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-md active:translate-y-0"
                          }`}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>

                  {timeSlots.length === 0 && (
                    <div className="py-8 text-center text-slate-500">
                      لا توجد أوقات متاحة لهذا اليوم.
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================
// مكوّنات مساعدة صغيرة لتنظيم الكود وتوحيد الشكل
// =========================

function SectionTitle({ icon, title }) {
  return (
    <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
      <span>{icon}</span>
      {title}
    </h2>
  );
}

function Banner({ tone, children }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-600",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };

  return (
    <div className={`mb-4 rounded-xl border p-3 text-sm ${tones[tone]}`}>
      {children}
    </div>
  );
}

function StepBadge({ label, active, show = true }) {
  if (!show) return null;
  return (
    <span
      className={`rounded-full px-3 py-1 ${
        active ? "bg-emerald-500 text-white" : "bg-white/70 text-slate-500"
      }`}
    >
      {label}
    </span>
  );
}

function StepDivider({ show = true }) {
  if (!show) return null;
  return <span className="h-px w-6 bg-white/50" />;
}
